import React, { memo, useState } from 'react';
import { Table, WithTableLayout } from '@qrios/frontend-library';
import { Box } from '@mui/material';
import endpoints from '../../../../../../api/endpoints';
import { mapApiDataIntoTableFormat } from './dataTransformator';
import { columns } from './columns';
import locales from './locales';
import { InvitedUserItem, InvitedUsersResponse } from './interfaces';
import { Dialog } from '../../../../../common/dialog';
import { useDeleteUserInvitation } from './user-invitation/hooks/useDeleteUserInvitation';
import DeleteUserInvitation from './user-invitation/delete';
import { useSendUserInvitation } from './user-invitation/hooks/useSendUserInvitation';
import { useResendUserInvitation } from './user-invitation/hooks/useResendUserInvitation';
import ResendInvitation from './user-invitation/resend';
import { useFetch } from '../../../../../../api/useFetch';
import UserInvitationDetails from './user-invitation/invite';
import TableFooter from '../active/table-footer';
import TableFooterInfo from '../../../../../common/table/footerInfo';

const InActiveUserTable = () => {
  const {
    data = {} as InvitedUsersResponse,
    refresh,
    loading,
    error,
    previousOptions
  } = useFetch<InvitedUsersResponse>({
    pathname: endpoints.accountsManagementInactive
  });

  const {
    userInvitationModalOpen,
    handleOpenUserInvitationModal,
    handleUserInvitation,
    handleCloseUserInvitationModal,
    inviteLoading,
    inviteError
  } = useSendUserInvitation({
    handleSuccess: () => {
      refresh();
    }
  });

  const {
    userResendInvitationModalOpen,
    handleResendUserInvitation,
    handleOpenResendUserInvitationModal,
    handleCloseResendUserInvitationModal,
    resendLoading,
    resendError
  } = useResendUserInvitation({
    handleSuccess: () => {
      refresh();
    }
  });

  const {
    deleteUserInvitationModalOpen,
    handleOpenDeleteUserInvitationModal,
    handleCloseDeleteUserInvitationModal,
    handleDeleteUserInvitation,
    deleteLoading,
    deleteError
  } = useDeleteUserInvitation({
    handleSuccess: () => {
      refresh();
    }
  });

  const [selectedInvitation, setSelectedInvitation] = useState<InvitedUserItem | undefined>();

  const handleOpenDeleteInvitation = (e: React.SetStateAction<InvitedUserItem>) => {
    setSelectedInvitation(e);
    handleOpenDeleteUserInvitationModal();
  };

  const handleResendInvitation = (e: React.SetStateAction<InvitedUserItem>) => {
    setSelectedInvitation(e);
    handleOpenResendUserInvitationModal();
  };

  const tableRowActions = {
    openResend: handleResendInvitation,
    openDelete: handleOpenDeleteInvitation
  };

  return (
    <WithTableLayout>
      <Table
        dataGridProps={{
          rows: !data?.settings || loading ? [] : mapApiDataIntoTableFormat(data, tableRowActions),
          columns,
          loading,
          error: {
            message: error,
            refresh: () => refresh(previousOptions)
          }
        }}
      />
      {!error && (
        <Box mr={3}>
          <TableFooter
            tableInfo={<TableFooterInfo label="User invitations" tooltip="" value="" />}
            actionTitle="Invite user"
            disabled={loading}
            refreshHandler={refresh}
            inviteAction={handleOpenUserInvitationModal}
          />
        </Box>
      )}
      <Dialog
        open={userInvitationModalOpen}
        handleClose={handleCloseUserInvitationModal}
        title={locales.dialog.details.title}
      >
        {data?.settings?.availableRoles?.length > 0 && (
          <UserInvitationDetails
            roles={data?.settings?.availableRoles}
            handleCloseDialog={handleCloseUserInvitationModal}
            handleSubmitAction={handleUserInvitation}
            loading={inviteLoading}
            error={inviteError}
          />
        )}
      </Dialog>

      <Dialog
        open={deleteUserInvitationModalOpen}
        handleClose={handleCloseDeleteUserInvitationModal}
        title={locales.deleteModal.title}
      >
        {selectedInvitation && (
          <DeleteUserInvitation
            item={selectedInvitation}
            handleCloseDialog={handleCloseDeleteUserInvitationModal}
            handleSubmitAction={handleDeleteUserInvitation}
            loading={deleteLoading}
            error={deleteError}
          />
        )}
      </Dialog>
      <Dialog
        open={userResendInvitationModalOpen}
        handleClose={handleCloseResendUserInvitationModal}
        title={locales.resendModal.title}
      >
        {selectedInvitation && (
          <ResendInvitation
            item={selectedInvitation}
            handleCloseDialog={handleCloseResendUserInvitationModal}
            handleSubmitAction={handleResendUserInvitation}
            loading={resendLoading}
            error={resendError}
          />
        )}
      </Dialog>
    </WithTableLayout>
  );
};

export default memo(InActiveUserTable);
